import { useLocation } from 'react-router-dom';
import { create } from 'zustand';
import { memo, useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Notification } from '@unified-checkout/design-system';
import type {
  INotification,
  TNotificationState,
} from 'libs/design-system/src/types/notification/notification';

import {
  DEFAULT_ICONS,
  INITIAL_STATES,
  NOTIFICATION_STATUS,
  NOTIFIER_TIMEOUT,
} from '@app/dashboard/constants/notifier';

export type TNotificationOptions = Omit<
  INotification,
  'title' | 'state' | 'show'
>;

export const useNotificationStore = create(() => INITIAL_STATES);

const hide = () => useNotificationStore.setState({ show: false });

const reset = () => useNotificationStore.setState(INITIAL_STATES);

const show = (
  state: TNotificationState,
  title: string,
  overrideProps: TNotificationOptions = {}
) => {
  useNotificationStore.setState({
    show: true,
    title,
    state: state as TNotificationState,
    overrideProps: {
      timeout: NOTIFIER_TIMEOUT,
      icon: DEFAULT_ICONS[state],
      onCloseHandler: () => {},
      ...overrideProps,
    },
  });
};

const createNotifier = (state: TNotificationState) => {
  return (title: string, options?: TNotificationOptions) => {
    show(state, title, options);
  };
};

export const notify = {
  success: createNotifier(NOTIFICATION_STATUS.SUCCESS),

  error: createNotifier(NOTIFICATION_STATUS.ERROR),

  warning: createNotifier(NOTIFICATION_STATUS.WARNING),

  primary: createNotifier(NOTIFICATION_STATUS.PRIMARY),

  normal: createNotifier(NOTIFICATION_STATUS.NORMAL),
};

const NotificationLocationWatcher = memo(() => {
  // spertaed from NotificationContent to prevent unnecessary re-renders while navigating between pages.
  const { pathname, search } = useLocation();

  useEffect(() => {
    hide();
  }, [pathname, search]);

  return null;
});

const NotificationContent = memo(() => {
  // shallow used to prevent unnecessary re-renders while navigating between pages.
  const { show, state, title, overrideProps } = useNotificationStore(
    useShallow((notify) => ({
      show: notify.show,
      state: notify.state,
      title: notify.title,
      overrideProps: notify.overrideProps,
    }))
  );  
    
  const handleClose = useCallback(() => {
    hide();
    overrideProps.onCloseHandler?.();
  }, [overrideProps.onCloseHandler]);

  useEffect(() => {
    if (!show) {
      // make sure the animation runs before reset the states
      const timer = setTimeout(() => reset(), 500);
      return () => clearTimeout(timer);
    }
  }, [show]);

  return (
    <Notification
      position="fixed"
      width="fit-content"
      type="notification"
      {...overrideProps}
      state={state}
      title={title}
      show={show}
      onCloseHandler={handleClose}
    />
  );
});

export const NotificationRenderer = memo(() => {
  return (
    <>
      <NotificationLocationWatcher />
      <NotificationContent />
    </>
  );
});
